import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit } from "react-icons/fa"
import { FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import {
  useDeleteClassSession,
  useUpdateClassSession,
} from "@/hooks/api/useClassSessions"
import type {
  ClassSessionResponse,
  ClassSessionUpdateInput,
} from "@/types/class_session"
import { class_sessionUpdateSchema } from "@/types/class_session"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { type Resolver, type SubmitHandler, useForm } from "react-hook-form"

interface ClassSessionActionsMenuProps {
  classSession: ClassSessionResponse
}

export const ClassSessionActionsMenu = memo(
  ({ classSession }: ClassSessionActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const deleteClassSessionMutation = useDeleteClassSession()
    const updateClassSessionMutation = useUpdateClassSession()

    const form = useForm<ClassSessionUpdateInput>({
      resolver: zodResolver(
        class_sessionUpdateSchema,
      ) as Resolver<ClassSessionUpdateInput>,
      mode: "onBlur",
      defaultValues: {
        class_template_id: classSession.class_template_id ?? "",
        instructor_id: classSession.instructor_id ?? "",
        session_date: classSession.session_date ?? "",
        session_start: classSession.session_start ?? "",
        session_end: classSession.session_end ?? "",
        status: classSession.status ?? null,
        cancellation_reason: classSession.cancellation_reason ?? "",
        location_override: classSession.location_override ?? "",
        max_students_override: classSession.max_students_override ?? null,
        notes: classSession.notes ?? "",
      },
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(
      classSession.class_template_id ?? classSession.id,
    )

    const handleDelete = async () => {
      try {
        await deleteClassSessionMutation.mutateAsync(classSession.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "ClassSession deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting class_session:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete class_session"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage =
            "You don't have permission to delete this class_session"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<ClassSessionUpdateInput> = async (
      data,
    ) => {
      try {
        await updateClassSessionMutation.mutateAsync({
          class_sessionId: classSession.id,
          class_sessionData: data,
        })
        setShowEditDialog(false)
        form.reset({
          class_template_id: classSession.class_template_id ?? "",
          instructor_id: classSession.instructor_id ?? "",
          session_date: classSession.session_date ?? "",
          session_start: classSession.session_start ?? "",
          session_end: classSession.session_end ?? "",
          status: classSession.status ?? null,
          cancellation_reason: classSession.cancellation_reason ?? "",
          location_override: classSession.location_override ?? "",
          max_students_override: classSession.max_students_override ?? null,
          notes: classSession.notes ?? "",
        })
        toaster.create({
          title: "ClassSession updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating class_session:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update class_session"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this class_session"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset({
        class_template_id: classSession.class_template_id ?? "",
        instructor_id: classSession.instructor_id ?? "",
        session_date: classSession.session_date ?? "",
        session_start: classSession.session_start ?? "",
        session_end: classSession.session_end ?? "",
        status: classSession.status ?? null,
        cancellation_reason: classSession.cancellation_reason ?? "",
        location_override: classSession.location_override ?? "",
        max_students_override: classSession.max_students_override ?? null,
        notes: classSession.notes ?? "",
      })
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit Class Meeting</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.class_template_id}
                  errorText={getErrorText(
                    form.formState.errors.class_template_id,
                  )}
                  label="Class Template Id"
                >
                  <Input
                    placeholder={"Enter class template id"}
                    aria-invalid={!!form.formState.errors.class_template_id}
                    {...form.register("class_template_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.instructor_id}
                  errorText={getErrorText(form.formState.errors.instructor_id)}
                  label="Instructor Id"
                >
                  <Input
                    placeholder={"Enter instructor id"}
                    aria-invalid={!!form.formState.errors.instructor_id}
                    {...form.register("instructor_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.session_date}
                  errorText={getErrorText(form.formState.errors.session_date)}
                  label="Meeting Date"
                >
                  <Input
                    type="date"
                    placeholder={"Select meeting date"}
                    aria-invalid={!!form.formState.errors.session_date}
                    {...form.register("session_date")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.session_start}
                  errorText={getErrorText(form.formState.errors.session_start)}
                  label="Meeting Start"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter meeting start"}
                    aria-invalid={!!form.formState.errors.session_start}
                    {...form.register("session_start")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.session_end}
                  errorText={getErrorText(form.formState.errors.session_end)}
                  label="Meeting End"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter meeting end"}
                    aria-invalid={!!form.formState.errors.session_end}
                    {...form.register("session_end")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.status}
                  errorText={getErrorText(form.formState.errors.status)}
                  label="Status"
                >
                  <select
                    {...form.register("status", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.status}
                    className="chakra-select"
                  >
                    {" "}
                    <option value="">Select Status</option>{" "}
                    <option value="scheduled">Scheduled</option>{" "}
                    <option value="in_progress">In Progress</option>{" "}
                    <option value="completed">Completed</option>{" "}
                    <option value="cancelled">Cancelled</option>{" "}
                    <option value="rescheduled">Rescheduled</option>{" "}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.cancellation_reason}
                  errorText={getErrorText(
                    form.formState.errors.cancellation_reason,
                  )}
                  label="Cancellation Reason"
                >
                  <Textarea
                    {...form.register("cancellation_reason")}
                    placeholder={"Enter cancellation reason"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.cancellation_reason}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.location_override}
                  errorText={getErrorText(
                    form.formState.errors.location_override,
                  )}
                  label="Location Override"
                >
                  <Input
                    placeholder={"Enter location override"}
                    aria-invalid={!!form.formState.errors.location_override}
                    {...form.register("location_override")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.max_students_override}
                  errorText={getErrorText(
                    form.formState.errors.max_students_override,
                  )}
                  label="Max Students Override"
                >
                  <Input
                    type="number"
                    step={1}
                    placeholder={"Enter max students override"}
                    aria-invalid={!!form.formState.errors.max_students_override}
                    {...form.register("max_students_override", {
                      valueAsNumber: true,
                    })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateClassSessionMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateClassSessionMutation.isPending}
                  loadingText="Updating..."
                >
                  Update Class Meeting
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Class Meeting</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The classSession will be
                permanently removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteClassSessionMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteClassSessionMutation.isPending}
                >
                  {deleteClassSessionMutation.isPending
                    ? "Deleting..."
                    : "Delete Class Meeting"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

ClassSessionActionsMenu.displayName = "ClassSessionActionsMenu"

export default ClassSessionActionsMenu
